import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { DemoScenarioId } from "@/lib/demoMode/scenarios";

const STORAGE_KEY = "demo-mode-scenario";

interface DemoModeContextValue {
  scenario: DemoScenarioId | null;
  setScenario: (s: DemoScenarioId | null) => void;
  isDemo: boolean;
}

const DemoModeContext = createContext<DemoModeContextValue | undefined>(undefined);

function readInitial(): DemoScenarioId | null {
  if (typeof window === "undefined") return null;
  try {
    const v = window.sessionStorage.getItem(STORAGE_KEY);
    if (v === "ready" || v === "almost" || v === "needs") return v;
  } catch { /* ignore */ }
  return null;
}

export const DemoModeProvider = ({ children }: { children: ReactNode }) => {
  const [scenario, setScenarioState] = useState<DemoScenarioId | null>(readInitial);

  const setScenario = useCallback((s: DemoScenarioId | null) => {
    setScenarioState(s);
    try {
      if (s) window.sessionStorage.setItem(STORAGE_KEY, s);
      else window.sessionStorage.removeItem(STORAGE_KEY);
    } catch { /* ignore */ }
  }, []);

  // Cross-tab sync within the same browser
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      const v = e.newValue;
      setScenarioState(v === "ready" || v === "almost" || v === "needs" ? v : null);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const value = useMemo<DemoModeContextValue>(
    () => ({ scenario, setScenario, isDemo: scenario !== null }),
    [scenario, setScenario],
  );

  return <DemoModeContext.Provider value={value}>{children}</DemoModeContext.Provider>;
};

export function useDemoMode(): DemoModeContextValue {
  const ctx = useContext(DemoModeContext);
  if (!ctx) return { scenario: null, setScenario: () => {}, isDemo: false };
  return ctx;
}